import {
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environment/environment';
import { ComponentDataSourceService } from '../../../../services/component-data-source/component-data-source.service';

export interface InvitationDetailModalData {
  invitationGuid: string;
  onDeleteSuccess?: () => void;
}

interface InvitationEvent {
  event_id: number;
  guid: string;
  start_date: string;
  end_date: string;
  event_type?: string;
  event_name?: string;
  location?: string;
}

interface Invitee {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface ResponseRow {
  user_id: number;
  event_id: number;
  selected: boolean;
  responded_at?: string;
}

interface AssignmentRow {
  user_id: number;
  event_id: number;
}

interface SearchUser {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
}

type DistributionMode = 'events' | 'hours';

@Component({
  selector: 'app-duna-event-instructors-invitation-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  templateUrl: './invitation-detail-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './invitation-detail-modal.component.scss',
})
export class DunaEventInstructorsInvitationDetailModalComponent
  implements OnInit, OnDestroy
{
  invitationGuid: string;
  loading = true;
  error: string | null = null;
  invitation: {
    id: number;
    guid: string;
    response_deadline: string;
    status: string;
    events: InvitationEvent[];
    invitees: Invitee[];
    responses: ResponseRow[];
    assignments: AssignmentRow[];
  } | null = null;
  assignmentMatrix: Record<string, Record<number, boolean>> = {}; // event_guid -> user_id -> checked
  savingAssignments = false;
  distributing = false;
  completing = false;
  deleting = false;
  actionError: string | null = null;
  distributionMode: DistributionMode = 'events';

  searchQuery = '';
  searchResults: SearchUser[] = [];
  searching = false;
  showSearchResults = false;
  addingInvitee = false;
  removingInviteeId: number | null = null;
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<DunaEventInstructorsInvitationDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InvitationDetailModalData,
    private http: HttpClient,
    private dataSourceService: ComponentDataSourceService,
    private snackBar: MatSnackBar,
    private ngZone: NgZone
  ) {
    this.invitationGuid = data?.invitationGuid ?? '';
  }

  get apiUrl(): string {
    return environment.apiUrl;
  }

  ngOnInit(): void {
    this.loadDetail();
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => this.doSearch(query));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchInput(value: string): void {
    this.searchQuery = value;
    if (value.trim().length < 2) {
      this.searchResults = [];
      this.showSearchResults = false;
      return;
    }
    this.searchSubject.next(value.trim());
  }

  private doSearch(name: string): void {
    this.searching = true;
    this.showSearchResults = true;
    this.http
      .get<{ users: SearchUser[] }>(
        `${this.apiUrl}/events/instructor-invitations/search-users`,
        {
          params: { name },
        }
      )
      .subscribe({
        next: (res) => {
          const currentIds = (this.invitation?.invitees ?? []).map(
            (i: Invitee) => i.user_id
          );
          this.searchResults = (res?.users ?? []).filter(
            (u) => !currentIds.includes(u.user_id)
          );
          this.searching = false;
        },
        error: () => {
          this.searching = false;
          this.searchResults = [];
          this.actionError = 'Søgning fejlede.';
        },
      });
  }

  addInvitee(user: SearchUser): void {
    this.actionError = null;
    this.addingInvitee = true;
    this.http
      .post<{ added: boolean }>(
        `${this.apiUrl}/events/instructor-invitations/${this.invitationGuid}/invitees`,
        { user_id: user.user_id }
      )
      .subscribe({
        next: () => {
          this.addingInvitee = false;
          this.searchQuery = '';
          this.searchResults = [];
          this.showSearchResults = false;
          this.loadDetail();
        },
        error: (err) => {
          this.addingInvitee = false;
          this.actionError =
            err?.error?.message || 'Kunne ikke tilføje instruktør.';
        },
      });
  }

  removeInvitee(u: Invitee): void {
    if (!confirm(`Fjern ${this.inviteeName(u)} fra invitationen?`)) return;
    this.ngZone.run(() => {
      this.actionError = null;
      this.removingInviteeId = u.user_id;
    });
    this.http
      .delete(
        `${this.apiUrl}/events/instructor-invitations/${this.invitationGuid}/invitees/${u.user_id}`
      )
      .subscribe({
        next: () => {
          this.removingInviteeId = null;
          this.loadDetail();
        },
        error: (err) => {
          this.removingInviteeId = null;
          this.actionError =
            err?.error?.message || 'Kunne ikke fjerne instruktør.';
        },
      });
  }

  displaySearchName(user: SearchUser): string {
    return (
      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
      user.email ||
      String(user.user_id)
    );
  }

  loadDetail(): void {
    this.loading = true;
    this.error = null;
    const obs = this.dataSourceService.executeDataSource(
      'instructor_invitation_detail',
      { guid: this.invitationGuid }
    );
    if (!obs) {
      this.loading = false;
      this.error = 'Kunne ikke hente invitation.';
      return;
    }
    obs.subscribe({
      next: (result: any) => {
        this.invitation = result?.invitation ?? null;
        this.loading = false;
        if (this.invitation) {
          this.buildAssignmentMatrix();
        } else {
          this.dialogRef.close(true);
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.message || 'Kunne ikke hente invitation.';
        if (err?.status === 404) {
          this.dialogRef.close(true);
        }
      },
    });
  }

  private buildAssignmentMatrix(): void {
    const inv = this.invitation!;
    const matrix: Record<string, Record<number, boolean>> = {};
    for (const ev of inv.events || []) {
      matrix[ev.guid] = {};
      for (const u of inv.invitees || []) {
        matrix[ev.guid][u.user_id] = (inv.assignments || []).some(
          (a: AssignmentRow) =>
            a.event_id === ev.event_id && a.user_id === u.user_id
        );
      }
    }
    this.assignmentMatrix = matrix;
  }

  isAssigned(eventGuid: string, userId: number): boolean {
    return !!this.assignmentMatrix[eventGuid]?.[userId];
  }

  setAssigned(eventGuid: string, userId: number, value: boolean): void {
    if (!this.assignmentMatrix[eventGuid])
      this.assignmentMatrix[eventGuid] = {};
    this.assignmentMatrix[eventGuid][userId] = value;
  }

  toggleAssignment(eventGuid: string, userId: number): void {
    const cur = this.isAssigned(eventGuid, userId);
    this.setAssigned(eventGuid, userId, !cur);
  }

  responded(eventId: number, userId: number): boolean {
    return (this.invitation?.responses || []).some(
      (r) => r.event_id === eventId && r.user_id === userId && r.selected
    );
  }

  inviteeName(inv: Invitee): string {
    return (
      [inv.first_name, inv.last_name].filter(Boolean).join(' ') ||
      inv.email ||
      String(inv.user_id)
    );
  }

  /** Event column label: date + start–end time + name (for matrix headers) */
  eventColumnLabel(ev: InvitationEvent): string {
    const name = ev.event_name || ev.guid || '';
    const timeRange = this.formatEventTimeRange(ev.start_date, ev.end_date);
    const duration = this.formatDurationFromMinutes(
      this.getEventDurationMinutes(ev),
      true
    );
    return `${timeRange} · ${duration} · ${name}`;
  }

  /** Compact date line for sticky matrix header (tooltip has full label). */
  eventColumnDateLine(ev: InvitationEvent): string {
    const start = this.parseApiDate(ev.start_date);
    if (!start) return '–';
    return start.toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
    });
  }

  /** Compact time line for sticky matrix header. */
  eventColumnTimeLine(ev: InvitationEvent): string {
    const start = this.parseApiDate(ev.start_date);
    const end = this.parseApiDate(ev.end_date);
    if (!start || !end) return '';
    const fmt = (d: Date) =>
      d.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
    return `${fmt(start)}–${fmt(end)}`;
  }

  statusLabel(status: string): string {
    const labels: Record<string, string> = {
      draft: 'Kladde',
      sent: 'Sendt',
      responses_closed: 'Svar lukket',
      completed: 'Afsluttet',
    };
    return labels[status] ?? status;
  }

  distribute(): void {
    this.distributeLocal(this.distributionMode);
  }

  private distributeLocal(mode: DistributionMode): void {
    this.actionError = null;
    const inv = this.invitation;
    if (!inv || inv.events.length === 0 || inv.invitees.length === 0) {
      return;
    }

    this.distributing = true;
    try {
      const matrix: Record<string, Record<number, boolean>> = {};
      const assignmentCountByUser = new Map<number, number>();
      const assignmentMinutesByUser = new Map<number, number>();
      const inviteeIds = inv.invitees.map((u) => u.user_id);

      for (const ev of inv.events) {
        matrix[ev.guid] = {};
        for (const uid of inviteeIds) {
          matrix[ev.guid][uid] = false;
        }
      }
      for (const uid of inviteeIds) {
        assignmentCountByUser.set(uid, 0);
        assignmentMinutesByUser.set(uid, 0);
      }

      const respondersByEvent = new Map<number, number[]>();
      for (const ev of inv.events) {
        respondersByEvent.set(ev.event_id, []);
      }
      for (const row of inv.responses || []) {
        if (!row.selected) {
          continue;
        }
        if (!respondersByEvent.has(row.event_id)) {
          continue;
        }
        respondersByEvent.get(row.event_id)!.push(row.user_id);
      }

      for (const ev of inv.events) {
        const candidates = (respondersByEvent.get(ev.event_id) || []).filter(
          (uid) => inviteeIds.includes(uid)
        );
        if (candidates.length === 0) {
          continue;
        }

        const eventMinutes = this.getEventDurationMinutes(ev);
        let selectedUserId = candidates[0];
        for (const candidateUserId of candidates) {
          const selectedCount = assignmentCountByUser.get(selectedUserId) ?? 0;
          const candidateCount =
            assignmentCountByUser.get(candidateUserId) ?? 0;
          const selectedMinutes =
            assignmentMinutesByUser.get(selectedUserId) ?? 0;
          const candidateMinutes =
            assignmentMinutesByUser.get(candidateUserId) ?? 0;

          const candidateIsBetter =
            mode === 'hours'
              ? candidateMinutes < selectedMinutes ||
                (candidateMinutes === selectedMinutes &&
                  candidateCount < selectedCount) ||
                (candidateMinutes === selectedMinutes &&
                  candidateCount === selectedCount &&
                  candidateUserId < selectedUserId)
              : candidateCount < selectedCount ||
                (candidateCount === selectedCount &&
                  candidateMinutes < selectedMinutes) ||
                (candidateCount === selectedCount &&
                  candidateMinutes === selectedMinutes &&
                  candidateUserId < selectedUserId);

          if (candidateIsBetter) {
            selectedUserId = candidateUserId;
          }
        }

        matrix[ev.guid][selectedUserId] = true;
        assignmentCountByUser.set(
          selectedUserId,
          (assignmentCountByUser.get(selectedUserId) ?? 0) + 1
        );
        assignmentMinutesByUser.set(
          selectedUserId,
          (assignmentMinutesByUser.get(selectedUserId) ?? 0) + eventMinutes
        );
      }

      this.assignmentMatrix = matrix;
    } catch (err: any) {
      this.actionError = err?.message || 'Kunne ikke fordele automatisk.';
    } finally {
      this.distributing = false;
    }
  }

  private buildAssignmentsPayload(): {
    event_id: number;
    user_id: number;
  }[] {
    const assignments: { event_id: number; user_id: number }[] = [];
    for (const ev of this.invitation?.events || []) {
      const row = this.assignmentMatrix[ev.guid];
      if (!row) continue;
      for (const [uidStr, checked] of Object.entries(row)) {
        if (checked) {
          assignments.push({ event_id: ev.event_id, user_id: +uidStr });
        }
      }
    }
    return assignments;
  }

  saveAssignments(): void {
    this.actionError = null;
    const assignments = this.buildAssignmentsPayload();
    this.savingAssignments = true;
    this.http
      .post(
        `${this.apiUrl}/events/instructor-invitations/${this.invitationGuid}/assignments`,
        { assignments }
      )
      .subscribe({
        next: () => {
          this.savingAssignments = false;
          this.loadDetail();
        },
        error: (err) => {
          this.savingAssignments = false;
          this.actionError =
            err?.error?.message || 'Kunne ikke gemme fordeling.';
        },
      });
  }

  complete(): void {
    this.actionError = null;
    this.completing = true;
    const assignments = this.buildAssignmentsPayload();
    this.http
      .post(
        `${this.apiUrl}/events/instructor-invitations/${this.invitationGuid}/complete`,
        { assignments }
      )
      .subscribe({
        next: () => {
          this.completing = false;
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.completing = false;
          this.actionError = err?.error?.message || 'Kunne ikke afslutte.';
        },
      });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  get canEdit(): boolean {
    return this.invitation?.status !== 'completed';
  }

  get canDelete(): boolean {
    return this.invitation?.status !== 'completed';
  }

  assignedMinutesForInvitee(userId: number): number {
    const inv = this.invitation;
    if (!inv) {
      return 0;
    }
    let total = 0;
    for (const ev of inv.events || []) {
      if (this.isAssigned(ev.guid, userId)) {
        total += this.getEventDurationMinutes(ev);
      }
    }
    return total;
  }

  assignedDurationLabelForInvitee(userId: number): string {
    return this.formatDurationFromMinutes(
      this.assignedMinutesForInvitee(userId)
    );
  }

  private parseApiDate(raw: string | null | undefined): Date | null {
    if (!raw) {
      return null;
    }
    const normalized = raw.trim().replace(' ', 'T');
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private formatEventTimeRange(
    startRaw: string | null | undefined,
    endRaw: string | null | undefined
  ): string {
    const start = this.parseApiDate(startRaw);
    const end = this.parseApiDate(endRaw);
    if (!start || !end) {
      return 'Tid ukendt';
    }
    const sameDay = start.toDateString() === end.toDateString();
    if (sameDay) {
      const date = start.toLocaleDateString('da-DK', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const startTime = start.toLocaleTimeString('da-DK', {
        hour: '2-digit',
        minute: '2-digit',
      });
      const endTime = end.toLocaleTimeString('da-DK', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${date} kl. ${startTime}-${endTime}`;
    }
    return `${start.toLocaleString('da-DK', {
      dateStyle: 'short',
      timeStyle: 'short',
    })} - ${end.toLocaleString('da-DK', {
      dateStyle: 'short',
      timeStyle: 'short',
    })}`;
  }

  private getEventDurationMinutes(ev: InvitationEvent): number {
    const start = this.parseApiDate(ev.start_date);
    const end = this.parseApiDate(ev.end_date);
    if (!start || !end) {
      return 0;
    }
    const minutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
    return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
  }

  private formatDurationFromMinutes(
    totalMinutes: number,
    includeUnknownFallback = false
  ): string {
    if (!Number.isFinite(totalMinutes) || totalMinutes < 0) {
      return includeUnknownFallback ? 'Varighed ukendt' : '0 timer';
    }
    if (totalMinutes === 0) {
      return includeUnknownFallback ? '0 timer' : '0 timer';
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hourLabel = `${hours} ${hours === 1 ? 'time' : 'timer'}`;
    if (minutes === 0) {
      return hourLabel;
    }
    return `${hourLabel} ${minutes} minutter`;
  }

  deleteInvitation(): void {
    if (
      !this.canDelete ||
      !confirm(
        'Er du sikker på at du vil slette denne invitation? Denne handling kan ikke fortrydes.'
      )
    ) {
      return;
    }
    const guid = this.invitationGuid;
    const onSuccess = this.data?.onDeleteSuccess;
    // confirm() causes Zone.js to lose the Angular zone context — run the close explicitly inside the zone.
    this.ngZone.run(() => {
      this.actionError = null;
      this.deleting = true;
      this.dialogRef.close(true);
    });
    this.http
      .delete(`${this.apiUrl}/events/instructor-invitations/${guid}`)
      .subscribe({
        next: () => {
          onSuccess?.();
        },
        error: (err) => {
          this.ngZone.run(() => {
            this.deleting = false;
            const msg = err?.error?.message || 'Kunne ikke slette invitation.';
            this.snackBar.open(msg, undefined, { duration: 5000 });
          });
        },
      });
  }
}
